import React, { useState } from 'react';
import axios from 'axios';
import '../styles/TwitterVerification.css';

const TwitterVerification = () => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError('');
    setResults(null);
    setLoading(true);

    try {
      const response = await axios.post('/api/twitter-verification/analyze', 
        { username }
      );

      if (response.data.success) {
        setResults(response.data);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 402) {
        setError(`Insufficient credits. You need ${err.response.data.creditsNeeded} credits.`);
      } else if (err.response?.status === 401) {
        setError('Please login to use this tool.');
      } else {
        setError(err.response?.data?.message || 'Failed to analyze account');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTrustScoreColor = (score) => {
    if (score >= 80) return '#10b981'; // accent-green/success
    if (score >= 60) return '#3b82f6'; // accent-blue
    if (score >= 40) return '#fbbf24'; // accent-yellow
    if (score >= 20) return '#f97316'; // accent-orange
    return '#ef4444'; // accent-red
  };

  return (
    <div className="twitter-verification-container">
      <div className="verification-header">
        <h1>🔍 Twitter Account Verification</h1>
        <p>Check if a Twitter account is legitimate and trustworthy</p>
      </div>

      <div className="verification-form-container">
        <form onSubmit={handleAnalyze} className="verification-form">
          <div className="form-group">
            <label htmlFor="username">
              Twitter Username *
              <span className="label-hint">Without @ symbol</span>
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="elonmusk"
              required
            />
          </div>

          <button type="submit" className="analyze-btn" disabled={loading}>
            {loading ? 'Analyzing...' : 'Analyze Account'}
          </button>
        </form>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}
      </div>

      {results && (
        <div className="results-container">
          {/* Accuracy Disclaimer */}
          <div className="accuracy-disclaimer">
            <div className="disclaimer-icon">⚠️</div>
            <div className="disclaimer-content">
              <h4>Important Disclaimer</h4>
              <p>
                This analysis is <strong>not 100% accurate</strong>. Long-term scammers may have well-established 
                profiles with decent statistics. Always do additional research, check reviews, and exercise 
                caution when dealing with anyone, regardless of their trust score.
              </p>
            </div>
          </div>

          {/* Account Analysis Results */}
          {results.accountData && (
            <div className="results-section account-section">
              <h2>📊 Account Analysis</h2>

              <div className="account-header">
                <div className="account-info">
                  <h3>
                    {results.accountData.displayName || results.accountData.username}
                    {results.accountData.isVerified && (
                      <span className="verified-badge" title="Verified Account">✓</span>
                    )}
                  </h3>
                  <p className="username-display">@{results.accountData.username}</p>
                  {results.accountData.bio && (
                    <p className="bio">{results.accountData.bio}</p>
                  )}
                </div>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{results.accountData.followers?.toLocaleString()}</div>
                  <div className="stat-label">Followers</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{results.accountData.following?.toLocaleString()}</div>
                  <div className="stat-label">Following</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{results.accountData.tweets?.toLocaleString()}</div>
                  <div className="stat-label">Tweets</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {results.accountData.accountAge ?
                      `${Math.floor(results.accountData.accountAge / 365)}y ${Math.floor((results.accountData.accountAge % 365) / 30)}m`
                      : 'N/A'}
                  </div>
                  <div className="stat-label">Account Age</div>
                </div>
              </div>

              {results.trustAnalysis && (
                <div className="trust-score-section">
                  <h3>Trust Score</h3>
                  <div className="trust-score-display">
                    <div className="score-circle" style={{ borderColor: getTrustScoreColor(results.trustAnalysis.score) }}>
                      <div className="score-value">{results.trustAnalysis.score}</div>
                      <div className="score-max">/ 100</div>
                    </div>
                    <div className="trust-level">
                      <div
                        className="trust-badge"
                        style={{ backgroundColor: getTrustScoreColor(results.trustAnalysis.score) }}
                      >
                        {results.trustAnalysis.trustLevel}
                      </div>
                    </div>
                  </div>

                  {results.trustAnalysis.factors && results.trustAnalysis.factors.length > 0 && (
                    <div className="factors-breakdown">
                      <h4>Score Breakdown</h4>
                      {results.trustAnalysis.factors.map((factor, index) => (
                        <div key={index} className="factor-item">
                          <div className="factor-header">
                            <span className="factor-name">{factor.factor}</span>
                            <span className="factor-points">{factor.points} / {factor.maxPoints}</span>
                          </div>
                          <div className="factor-detail">{factor.detail}</div>
                          <div className="factor-bar">
                            <div
                              className="factor-bar-fill"
                              style={{ width: `${(factor.points / factor.maxPoints) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {results.trustAnalysis.warnings && results.trustAnalysis.warnings.length > 0 && (
                    <div className="warnings-section">
                      <h4>⚠️ Warnings</h4>
                      <ul className="warnings-list">
                        {results.trustAnalysis.warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Vouch Analysis Results */}
          {results.vouchAnalysis && (
            <div className="results-section vouch-section">
              <h2>🏆 Vouch Tag Analysis</h2>

              <div className="vouch-summary">
                <div className="vouch-tag-display">
                  <span className="tag-icon">#</span>
                  <span className="tag-text">{results.vouchAnalysis.vouchTag.replace('#', '')}</span>
                </div>

                <div className="vouch-stats">
                  <div className="vouch-stat-large">
                    <div className="vouch-count">{results.vouchAnalysis.vouchCount}</div>
                    <div className="vouch-label">Total Vouches Found</div>
                  </div>

                  <div className="credibility-score">
                    <div className="credibility-circle">
                      <div className="credibility-value">{results.vouchAnalysis.credibilityScore}</div>
                      <div className="credibility-max">/ 100</div>
                    </div>
                    <div className="credibility-level">{results.vouchAnalysis.credibilityLevel}</div>
                  </div>
                </div>
              </div>

              {results.vouchAnalysis.recentVouches && results.vouchAnalysis.recentVouches.length > 0 && (
                <div className="recent-vouches">
                  <h4>Recent Vouch Posts</h4>
                  <div className="vouches-list">
                    {results.vouchAnalysis.recentVouches.map((vouch, index) => (
                      <div key={index} className="vouch-item">
                        <div className="vouch-content">{vouch.content}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Combined Recommendation */}
          {results.recommendation && (
            <div className="results-section recommendation-section">
              <h2>💡 Overall Recommendation</h2>
              <div
                className="recommendation-card"
                style={{ borderLeftColor: results.recommendation.color }}
              >
                <div className="recommendation-level">{results.recommendation.level}</div>
                <div className="recommendation-message">{results.recommendation.message}</div>
                {results.combinedScore && (
                  <div className="combined-score">
                    Combined Score: <strong>{results.combinedScore}/100</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="results-footer">
            <small>
              ℹ️ This analysis is based on publicly available information and should be used as one factor in your decision-making process.
              <br />
              Analysis performed on: {new Date(results.analyzedAt).toLocaleString()}
            </small>
          </div>
        </div>
      )}

      <div className="info-section">
        <h2>ℹ️ How It Works</h2>
        <div className="info-grid">
          <div className="info-card">
            <h3>🔍 Account Analysis</h3>
            <p>
              Analyzes account age, follower/following ratio, activity level, verification status,
              and profile completeness to calculate a trust score out of 100.
            </p>
          </div>
          <div className="info-card">
            <h3>📊 Score Breakdown</h3>
            <p>
              View detailed point distribution across account age (30pts), followers (25pts), 
              tweet activity (30pts), and profile completeness (20pts).
            </p>
          </div>
          <div className="info-card">
            <h3>💯 Trust Assessment</h3>
            <p>
              Combines multiple factors to provide an overall trust level: Highly Trusted (85+), 
              Trusted (65+), Caution (40+), or High Risk (below 40).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwitterVerification;
